import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, InputAdornment, TextField } from "@mui/material";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { CreditsInfo, HubPlan } from "data/models";
import { ReactQueryMutationError, updateOverageLimit } from "data/mutations";
import { fetchCredits } from "data/queries";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { roundDownOverageBudget } from "utils/calculations";
import { formatPrice } from "utils/format";
import { resolveIdentityId } from "utils/identity";

interface OverageFieldProps {
  currentOverage: CreditsInfo;
  plan: HubPlan;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const OverageField = (props: OverageFieldProps) => {
  const [newOverageBudget, setNewOverageBudget] = useState(
    props.currentOverage.overageDollarsLimit
  );

  const queryClient = useQueryClient();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const creditsQuery = useQuery([QueryKeys.overage, identityId], () =>
    fetchCredits()
  );

  const overageMutation = useMutation(
    () => {
      if (newOverageBudget === undefined) {
        throw Error(
          "Unable to update overage, incomplete overage information."
        );
      }

      return updateOverageLimit(newOverageBudget, {
        overageRate: props.plan.overage_cost_dollars!,
        planCredits: props.plan.credit_amount,
      });
    },
    {
      onSuccess: (newOverage) => {
        queryClient.setQueryData([QueryKeys.credits, identityId], newOverage);
        setNewOverageBudget(newOverage.overageDollarsLimit);
        if (props.onSubmit) {
          props.onSubmit();
        }
      },
    }
  );

  const roundedBudget = roundDownOverageBudget(newOverageBudget, {
    overageRate: props.plan.overage_cost_dollars!,
    planCredits: props.plan.credit_amount,
  });

  const validLimit =
    creditsQuery.isSuccess &&
    newOverageBudget >= creditsQuery.data?.overageDollarsSpent;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          label="Overage Limit"
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                {`/${props.plan.cadence}`}
              </InputAdornment>
            ),
            inputProps: { min: 0, max: 10000 },
          }}
          defaultValue={formatPrice(
            creditsQuery.data?.overageDollarsLimit ?? 0
          )}
          value={newOverageBudget}
          onChange={(event) => {
            setNewOverageBudget(Math.min(parseInt(event.target.value), 10000));
          }}
        />
        <LoadingButton
          size="small"
          disabled={!validLimit}
          loading={overageMutation.isLoading}
          onClick={() => {
            overageMutation.mutate();
          }}
        >
          Save
        </LoadingButton>
        {props.onCancel && (
          <Button
            size="small"
            variant="text"
            color="secondary"
            disabled={overageMutation.isLoading}
            onClick={props.onCancel}
          >
            Cancel
          </Button>
        )}
      </Box>
      {!Number.isNaN(newOverageBudget) &&
        newOverageBudget !== roundedBudget &&
        newOverageBudget % props.plan.overage_cost_dollars! !== 0 && (
          <Alert severity="info" sx={{ marginBottom: "16px" }}>
            The overage limit will automatically be rounded down to the nearest{" "}
            ${props.plan.overage_cost_dollars! * 1000} increment the equivalent
            of 1000 credits ({formatPrice(roundedBudget)}) when saved.
          </Alert>
        )}
      {creditsQuery.isSuccess &&
        !Number.isNaN(newOverageBudget) &&
        !validLimit && (
          <Alert severity="warning" sx={{ marginBottom: "16px" }}>
            Cannot lower overage limit ({formatPrice(roundedBudget)}) to less
            than the current overage spend (
            {formatPrice(creditsQuery.data.overageDollarsSpent)})
          </Alert>
        )}
      {overageMutation.isError && (
        <Alert variant="outlined" severity="error">
          {(overageMutation.error as ReactQueryMutationError).message}
        </Alert>
      )}
    </Box>
  );
};
